import { authenticationStore } from "@/state/auth/AuthStore";
import { AuthenticationContext, UserOffice } from "@/type/core/auth/auth.types";
import { useUnit } from "effector-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { userOfficesStore } from "@state/offices/OfficesStore";
import { setSelectedOfficeInStore } from "@state/offices/OfficesEvents";

interface UseOfficeToDisplayReturn {
  selectedOffice: UserOffice | undefined;
  setSelectedOffice: Dispatch<SetStateAction<UserOffice | undefined>>;
  officesToDisplay: UserOffice[];
  handleTabClick: (activeKey: string) => void;
  activeKey: string;
}

const useOfficesToDisplay = (
  officeIdFromParams?: string,
): UseOfficeToDisplayReturn => {
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const userOfficeStore = useUnit(userOfficesStore);
  const officesToDisplay = useMemo(
    () =>
      [
        ...(authStore?.user?.offices ?? []),
        ...(authStore?.user?.officesForCgu?.filter((officeForCgu) =>
          authStore?.user?.offices.every(
            (office) => office.id !== officeForCgu.id,
          ),
        ) ?? []),
      ].sort((a, b) => a.alias.localeCompare(b.alias)),
    [authStore?.user?.offices, authStore?.user?.officesForCgu],
  );

  const selectedOfficeFromId = officesToDisplay.find(
    (office) => office.id === officeIdFromParams,
  );
  if (
    selectedOfficeFromId &&
    selectedOfficeFromId !== userOfficeStore.selectedOffice
  ) {
    setSelectedOfficeInStore(selectedOfficeFromId);
  }
  const [selectedOffice, setSelectedOffice] = useState<UserOffice | undefined>(
    selectedOfficeFromId
      ? selectedOfficeFromId
      : userOfficeStore.selectedOffice
        ? userOfficeStore.selectedOffice
        : officesToDisplay.length > 0
          ? officesToDisplay[0]
          : undefined,
  );

  const handleTabClick = (activeKey: string) => {
    setSelectedOffice(officesToDisplay[Number(activeKey)]);
    setSelectedOfficeInStore(officesToDisplay[Number(activeKey)]);
  };

  return {
    selectedOffice,
    setSelectedOffice,
    officesToDisplay,
    handleTabClick,
    activeKey: officesToDisplay
      .findIndex((office) => office.id === selectedOffice?.id)
      .toString(),
  };
};

export default useOfficesToDisplay;
